import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../hooks/redux';
import { useTutorial } from '../hooks/useTutorial';
import DesktopAppsModal from './DesktopAppsModal';

const DESKTOP_APPS_NUDGE_STORAGE_PREFIX = 'salesguru_desktop_apps_nudge_shown_';

/** Per-user flag so the post-onboarding nudge shows once, not on every login. */
export const getDesktopAppsNudgeStorageKey = (firebaseUid: string): string =>
  `${DESKTOP_APPS_NUDGE_STORAGE_PREFIX}${firebaseUid}`;

const hasSeenDesktopAppsNudge = (firebaseUid: string): boolean => {
  try {
    return localStorage.getItem(getDesktopAppsNudgeStorageKey(firebaseUid)) !== null;
  } catch (storageError) {
    console.error('[DesktopAppsNudge] Failed to read nudge flag, skipping nudge:', storageError);
    // Fail closed: without a readable guard the nudge would show on every
    // login, so treat unreadable storage as "already seen".
    return true;
  }
};

const markDesktopAppsNudgeSeen = (firebaseUid: string): void => {
  try {
    localStorage.setItem(getDesktopAppsNudgeStorageKey(firebaseUid), 'true');
  } catch (storageError) {
    console.error('[DesktopAppsNudge] Failed to persist nudge flag:', storageError);
  }
};

/**
 * Shows the desktop-apps download nudge once, right after a new user finishes
 * onboarding (the click-through tutorial completed or skipped — both persist
 * `preferences.tutorialCompleted`). Mounted above the routes so it survives
 * the navigation completeTutorial performs.
 *
 * Purely a nudge: it never blocks anything and never reappears for a user who
 * has already seen it (per-user localStorage flag).
 */
const DesktopAppsOnboardingNudge: React.FC = () => {
  const { mongoUser } = useAppSelector((state) => state.auth);
  const { isTutorialCompleted } = useTutorial();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Only a false -> true transition observed for a loaded user counts as
  // "onboarding finished this session". Without this, an existing user whose
  // profile loads with tutorialCompleted already true would trigger the nudge.
  const wasTutorialIncompleteRef = useRef(false);

  useEffect(() => {
    if (!mongoUser) {
      return;
    }
    if (!isTutorialCompleted) {
      wasTutorialIncompleteRef.current = true;
      return;
    }
    if (!wasTutorialIncompleteRef.current) {
      return;
    }
    wasTutorialIncompleteRef.current = false;

    if (hasSeenDesktopAppsNudge(mongoUser.firebaseUid)) {
      return;
    }
    // Mark seen at show time so the nudge stays one-shot even if the user
    // closes the tab instead of dismissing the dialog.
    markDesktopAppsNudgeSeen(mongoUser.firebaseUid);
    setIsModalOpen(true);
  }, [mongoUser, isTutorialCompleted]);

  return <DesktopAppsModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />;
};

export default DesktopAppsOnboardingNudge;
